import { routePaths } from "../constants/routes";

export const automationClusterInternalLinks = [
  { label: "Learn Manual Testing", to: routePaths.manualTesting },
  { label: "Learn API Testing", to: routePaths.apiTesting },
  { label: "Testing Interview Questions", to: routePaths.interviewQuestions },
];

const defaultAdvantages = [
  "Runs repetitive regression checks faster than manual execution.",
  "Improves release confidence when tests are stable, reviewed, and part of CI/CD.",
  "Helps QA teams validate important user journeys across browsers and builds.",
  "Creates reusable test coverage for login, forms, checkout, API flows, and smoke suites.",
];

const defaultDisadvantages = [
  "Automation needs maintenance when UI, locators, data, or business rules change.",
  "Poor waits, weak locators, and unstable environments can create flaky test results.",
  "Automation is not a replacement for exploratory testing, usability review, or requirement thinking.",
];

function faqFor(keyword) {
  return [
    {
      question: `Is ${keyword} good for beginners?`,
      answer: `Yes. ${keyword} is beginner-friendly when you first understand manual testing basics, locators, test cases, and simple browser workflows.`,
    },
    {
      question: `Do I need coding for ${keyword}?`,
      answer: "Basic coding is useful for automation testing. Start with variables, conditions, loops, functions, classes, and simple test assertions.",
    },
    {
      question: `How should I practice ${keyword}?`,
      answer: "Practice on real UI flows such as login, forms, alerts, tables, frames, drag and drop, file upload, and API-backed user journeys.",
    },
  ];
}

function makeAutomationPage({
  slug,
  title,
  description,
  keyword,
  what,
  why,
  types,
  example,
  questions,
}) {
  return {
    slug,
    path: `/${slug}`,
    eyebrow: "Automation Testing SEO Cluster",
    title,
    description,
    keyword,
    sections: [
      {
        id: "what-is-it",
        heading: `What is ${keyword}?`,
        paragraphs: what,
      },
      {
        id: "why-important",
        heading: "Why important",
        paragraphs: why,
      },
      {
        id: "types",
        heading: "Types",
        list: types,
      },
      {
        id: "example",
        heading: "Example",
        paragraphs: example,
      },
      {
        id: "advantages",
        heading: "Advantages",
        list: defaultAdvantages,
      },
      {
        id: "disadvantages",
        heading: "Disadvantages",
        list: defaultDisadvantages,
      },
      {
        id: "interview-questions",
        heading: "Interview Questions",
        list: questions,
      },
      {
        id: "faq",
        heading: "FAQ",
        faqs: faqFor(keyword),
      },
    ],
  };
}

export const automationTestingClusterPages = [
  makeAutomationPage({
    slug: "automation-testing-tutorial",
    title: "Automation Testing Tutorial for Beginners",
    description: "Learn automation testing step by step with tools, frameworks, Selenium, Playwright, examples, advantages, disadvantages, interview questions, and FAQs.",
    keyword: "Automation Testing Tutorial",
    what: [
      "Automation testing is the process of using tools and scripts to execute test cases automatically. Instead of repeating the same checks manually, testers write scripts that open the application, perform actions, validate results, and report pass or fail status.",
      "A good automation testing tutorial starts with testing basics, then moves into locators, waits, assertions, test data, framework structure, reporting, and CI/CD integration.",
    ],
    why: [
      "Automation testing is important because modern teams release frequently and cannot manually repeat every regression test for every build.",
      "It gives QA engineers a practical way to protect critical flows such as login, signup, checkout, search, API validation, and smoke testing.",
    ],
    types: ["UI automation", "API automation", "Regression automation", "Smoke automation", "Data-driven automation", "Cross-browser automation"],
    example: [
      "Example: An automated login test opens the login page, enters valid credentials, clicks login, waits for the dashboard, and checks that the dashboard title or user name is visible.",
      "The same test can be executed every time developers deploy a new build, which saves time and catches regressions quickly.",
    ],
    questions: ["What is automation testing?", "When should we automate a test case?", "What are the benefits of automation testing?", "What is the difference between Selenium and Playwright?", "What is a test automation framework?"],
  }),
  makeAutomationPage({
    slug: "selenium-tutorial",
    title: "Selenium Tutorial for Beginners",
    description: "Learn Selenium WebDriver basics, browser automation, locators, waits, alerts, frames, actions, Page Object Model, and interview-ready examples.",
    keyword: "Selenium Tutorial",
    what: [
      "Selenium is an open-source browser automation tool used to automate web applications. Selenium WebDriver can launch browsers, find elements, click buttons, type text, handle dropdowns, manage alerts, switch frames, and verify page behavior.",
      "A Selenium tutorial usually covers setup, WebDriver commands, locators, waits, assertions, TestNG or JUnit, Page Object Model, and reporting.",
    ],
    why: [
      "Selenium is important because many QA automation jobs still ask for WebDriver knowledge, locator strategy, synchronization, and framework design.",
      "Learning Selenium helps students understand how automation interacts with real browsers and web elements.",
    ],
    types: ["Selenium WebDriver", "Selenium Grid", "Selenium IDE", "Page Object Model", "Data-driven Selenium", "Hybrid Selenium framework"],
    example: [
      "Example: A Selenium test can find an email field by ID, type an email, find a password field, type a password, click login, and assert that the dashboard URL or heading is correct.",
      "For dynamic pages, Selenium tests should use explicit waits instead of fixed sleep so tests remain faster and more reliable.",
    ],
    questions: ["What is Selenium WebDriver?", "What are locators in Selenium?", "Difference between implicit wait and explicit wait?", "How do you handle alerts in Selenium?", "What is Page Object Model?"],
  }),
  makeAutomationPage({
    slug: "selenium-interview-questions",
    title: "Selenium Interview Questions and Answers",
    description: "Prepare Selenium interview questions on WebDriver, locators, waits, alerts, frames, actions, JavaScriptExecutor, Page Object Model, and frameworks.",
    keyword: "Selenium Interview Questions",
    what: [
      "Selenium interview questions test whether a candidate can automate real web scenarios, not just remember tool names. Interviewers usually ask about locators, waits, dropdowns, alerts, frames, windows, screenshots, JavaScriptExecutor, and framework design.",
      "Strong answers should include a simple definition, practical example, and the reason a concept is used in real projects.",
    ],
    why: [
      "Selenium is a common automation interview topic for QA engineers, SDET roles, and manual testers moving into automation.",
      "Preparing questions helps learners connect Selenium commands with test cases, debugging, flakiness, and maintainable framework structure.",
    ],
    types: ["Beginner Selenium questions", "Locator questions", "Wait and synchronization questions", "Framework questions", "Scenario-based Selenium questions"],
    example: [
      "Example answer: Explicit wait waits for a specific condition such as element visibility, clickability, or URL change. It is better than Thread.sleep because it waits only as long as needed.",
      "Another example: Page Object Model separates page locators and page actions from test logic, making automation code easier to maintain.",
    ],
    questions: ["What is Selenium?", "What is WebDriver?", "XPath vs CSS selector?", "How do you handle dynamic elements?", "How do you reduce flaky tests?", "What is POM?", "How do you run tests in parallel?"],
  }),
  makeAutomationPage({
    slug: "xpath-tutorial",
    title: "XPath Tutorial for Selenium Automation",
    description: "Learn XPath for Selenium with absolute XPath, relative XPath, contains, starts-with, text, axes, dynamic elements, and practical locator examples.",
    keyword: "XPath Tutorial",
    what: [
      "XPath is a locator language used to find elements in an HTML or XML document. In Selenium, XPath helps testers identify buttons, inputs, links, labels, tables, and dynamic elements when simple IDs or names are not available.",
      "A practical XPath tutorial teaches relative XPath, attributes, text matching, contains, starts-with, parent-child relationships, and axes.",
    ],
    why: [
      "XPath is important because real applications often have dynamic IDs, repeated components, nested forms, tables, and changing UI structures.",
      "Good XPath skills help automation testers write stable locators and avoid brittle scripts.",
    ],
    types: ["Absolute XPath", "Relative XPath", "Attribute XPath", "Text XPath", "Contains XPath", "Starts-with XPath", "XPath axes"],
    example: [
      "Example: //input[@name='email'] finds an input field with name email. //button[contains(text(),'Login')] finds a login button by visible text.",
      "For dynamic values, contains and starts-with are useful when only part of an attribute is stable.",
    ],
    questions: ["What is XPath?", "Absolute XPath vs relative XPath?", "How do you write XPath using contains?", "What are XPath axes?", "How do you handle dynamic elements with XPath?"],
  }),
  makeAutomationPage({
    slug: "test-automation-framework",
    title: "Test Automation Framework Guide",
    description: "Learn test automation framework design with Page Object Model, data-driven testing, keyword-driven testing, hybrid framework, reporting, CI/CD, and examples.",
    keyword: "Test Automation Framework",
    what: [
      "A test automation framework is a structured set of folders, coding standards, utilities, reusable functions, test data, reports, and execution rules used to build maintainable automated tests.",
      "Instead of writing random scripts, a framework gives the team a clean way to organize pages, tests, assertions, configuration, test data, logs, screenshots, and reports.",
    ],
    why: [
      "Frameworks are important because automation projects grow quickly. Without structure, scripts become hard to debug, update, review, and reuse.",
      "A good framework also supports parallel execution, environment switching, CI/CD integration, reporting, and stable regression testing.",
    ],
    types: ["Page Object Model", "Data-driven framework", "Keyword-driven framework", "Hybrid framework", "BDD framework", "Modular framework"],
    example: [
      "Example: In a Page Object Model framework, LoginPage stores login locators and actions, while LoginTest contains test steps and assertions.",
      "This separation makes code easier to maintain when the UI changes because locator updates stay inside the page class.",
    ],
    questions: ["What is a test automation framework?", "What is Page Object Model?", "Data-driven vs keyword-driven framework?", "What should a framework include?", "How do you integrate automation with CI/CD?"],
  }),
  makeAutomationPage({
    slug: "automation-testing-tools",
    title: "Automation Testing Tools List",
    description: "Compare popular automation testing tools including Selenium, Playwright, Cypress, Appium, Postman, Rest Assured, JMeter, and CI/CD tools.",
    keyword: "Automation Testing Tools",
    what: [
      "Automation testing tools help QA teams automate UI, API, mobile, performance, and regression testing. Different tools solve different testing problems, so tool choice should depend on project needs.",
      "For web UI, Selenium, Playwright, and Cypress are popular. For API testing, Postman and Rest Assured are common. For mobile, Appium is widely used.",
    ],
    why: [
      "Knowing automation tools is important because job descriptions often mention Selenium, Playwright, Cypress, Postman, Rest Assured, Git, Jenkins, and reporting tools.",
      "The best QA engineers understand not just tool names, but when to use each tool and what problem it solves.",
    ],
    types: ["Web UI tools", "API automation tools", "Mobile automation tools", "Performance testing tools", "CI/CD tools", "Reporting tools"],
    example: [
      "Example: Use Selenium or Playwright for browser regression, Postman for quick API validation, Rest Assured for API automation, Appium for mobile app automation, and Jenkins or GitHub Actions for scheduled test execution.",
    ],
    questions: ["Which automation tool is best for beginners?", "Selenium vs Playwright?", "Cypress vs Selenium?", "Which tool is used for API automation?", "What tools are needed in an automation framework?"],
  }),
  makeAutomationPage({
    slug: "playwright-tutorial",
    title: "Playwright Tutorial for Beginners",
    description: "Learn Playwright automation with locators, auto-waiting, assertions, fixtures, tracing, screenshots, API testing support, and practical examples.",
    keyword: "Playwright Tutorial",
    what: [
      "Playwright is a modern automation framework for testing web applications across Chromium, Firefox, and WebKit. It supports reliable locators, auto-waiting, tracing, screenshots, videos, parallel execution, and API testing.",
      "A Playwright tutorial helps learners write cleaner tests with fewer flaky waits because Playwright waits for many browser conditions automatically.",
    ],
    why: [
      "Playwright is important because many modern QA teams prefer fast, reliable, cross-browser automation with strong debugging tools.",
      "It is especially useful for applications with dynamic UI, single-page app behavior, network calls, and complex user flows.",
    ],
    types: ["Playwright UI testing", "Playwright API testing", "Cross-browser testing", "Parallel execution", "Trace viewer debugging", "Page Object Model with Playwright"],
    example: [
      "Example: A Playwright test can open a login page, fill email and password using getByLabel or locator, click login, and expect the dashboard heading to be visible.",
      "When a test fails, trace viewer can help inspect screenshots, actions, DOM snapshots, and network activity.",
    ],
    questions: ["What is Playwright?", "Why is Playwright less flaky?", "What is auto-waiting?", "How do Playwright locators work?", "Playwright vs Selenium?"],
  }),
  makeAutomationPage({
    slug: "cypress-tutorial",
    title: "Cypress Tutorial for Beginners",
    description: "Learn Cypress automation basics, commands, selectors, assertions, fixtures, intercepts, test runner, debugging, and practical UI testing examples.",
    keyword: "Cypress Tutorial",
    what: [
      "Cypress is a JavaScript-based testing tool used mainly for modern web application testing. It runs in the browser, provides a visual test runner, and makes it easy to debug UI tests.",
      "A Cypress tutorial usually covers installation, test structure, commands, selectors, assertions, fixtures, intercepts, custom commands, and reports.",
    ],
    why: [
      "Cypress is important because it gives fast feedback for frontend teams and makes it easy to write readable tests for UI behavior.",
      "It is useful for validating forms, dashboards, user flows, network stubbing, and frontend regression checks.",
    ],
    types: ["Component testing", "End-to-end testing", "API intercept testing", "Fixture-based testing", "Custom command testing", "Regression testing"],
    example: [
      "Example: A Cypress test visits the login page, gets the email input, types a valid email, types a password, clicks login, and checks that the dashboard URL or heading appears.",
      "Using cy.intercept, testers can observe or mock API responses during UI flows.",
    ],
    questions: ["What is Cypress?", "Cypress vs Selenium?", "What is cy.intercept?", "How do assertions work in Cypress?", "What are Cypress fixtures?"],
  }),
  makeAutomationPage({
    slug: "automation-vs-manual-testing",
    title: "Automation Testing vs Manual Testing",
    description: "Compare automation testing and manual testing with practical examples, when to automate, when to test manually, advantages, disadvantages, and FAQs.",
    keyword: "Automation Testing vs Manual Testing",
    what: [
      "Automation testing uses scripts and tools to execute repeatable checks, while manual testing relies on human testers to explore, validate, and judge application behavior.",
      "Both approaches are important. Manual testing helps understand requirements and user experience. Automation testing helps repeat stable regression checks quickly.",
    ],
    why: [
      "This comparison is important because good QA strategy is not about choosing only one approach. It is about using manual testing where human judgement is needed and automation where repeatability is valuable.",
      "Interviewers ask this topic to check whether a tester understands real project tradeoffs.",
    ],
    types: ["Manual exploratory testing", "Manual functional testing", "Automated smoke testing", "Automated regression testing", "API automation", "Hybrid QA strategy"],
    example: [
      "Example: A new payment flow should first be tested manually for requirements, UX, validations, edge cases, and business rules. After the flow becomes stable, high-value regression cases can be automated.",
    ],
    questions: ["Automation testing vs manual testing?", "Can automation replace manual testing?", "Which tests should be automated?", "When should we not automate?", "Why do teams need both manual and automation testing?"],
  }),
];

export function getAutomationTestingClusterPage(slug) {
  return automationTestingClusterPages.find((page) => page.slug === slug);
}
